import axios from "axios";

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:5000/api",
  headers: { "Content-Type": "application/json" },
});

API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

// Auth API
export const authAPI = {
  login: (data) => API.post("/auth/login", data),
  me: () => API.get("/auth/me"),
  logout: () => API.post("/auth/logout"),
};

// Admissions API
export const admissionAPI = {
  getAll: (params) => API.get("/admissions", { params }),
  getById: (id) => API.get(`/admissions/${id}`),
  create: (data) =>
    API.post("/admissions", data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  updateStatus: (id, status) => API.put(`/admissions/${id}/status`, { status }),
  delete: (id) => API.delete(`/admissions/${id}`),
};

// Notices API
export const noticeAPI = {
  getAll: (params) => API.get("/notices", { params }),
  getAdmin: (params) => API.get("/notices/admin", { params }),
  getById: (id) => API.get(`/notices/${id}`),
  create: (data) => API.post("/notices", data),
  update: (id, data) => API.put(`/notices/${id}`, data),
  togglePublish: (id) => API.patch(`/notices/${id}/toggle`),
  delete: (id) => API.delete(`/notices/${id}`),
};

// Events API
export const eventAPI = {
  getUpcoming: () => API.get("/events/upcoming"),
  getAll: (params) => API.get("/events", { params }),
  getAdmin: (params) => API.get("/events/admin", { params }),
  getById: (id) => API.get(`/events/${id}`),
  create: (data) =>
    API.post(
      "/events",
      data,
      data instanceof FormData
        ? { headers: { "Content-Type": "multipart/form-data" } }
        : {},
    ),
  update: (id, data) =>
    API.put(
      `/events/${id}`,
      data,
      data instanceof FormData
        ? { headers: { "Content-Type": "multipart/form-data" } }
        : {},
    ),
  uploadBulkImages: (id, data) =>
    API.post(`/events/bulk-images/${id}`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  togglePublish: (id) => API.patch(`/events/${id}/toggle-publish`),
  delete: (id) => API.delete(`/events/${id}`),
};

// Gallery API
export const galleryAPI = {
  getAll: (params) => API.get("/gallery", { params }),
  getAdmin: (params) => API.get("/gallery/admin", { params }),
  getById: (id) => API.get(`/gallery/${id}`),
  upload: (data) =>
    API.post("/gallery", data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  uploadBulk: (data) =>
    API.post("/gallery/bulk", data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  update: (id, data) => API.put(`/gallery/${id}`, data),
  togglePublish: (id) => API.patch(`/gallery/${id}/toggle-publish`),
  delete: (id) => API.delete(`/gallery/${id}`),
};

// Results API
export const resultAPI = {
  search: (params) => API.get("/results/search", { params }),
  getSections: (params) => API.get("/results/sections", { params }),
  getAll: (params) => API.get("/results", { params }),
  getById: (id) => API.get(`/results/${id}`),
  create: (data) => API.post("/results", data),
  update: (id, data) => API.put(`/results/${id}`, data),
  togglePublish: (id) => API.patch(`/results/${id}/publish`),
  delete: (id) => API.delete(`/results/${id}`),
  downloadTemplate: () =>
    API.get("/results/template", { responseType: "blob" }),
  uploadExcel: (formData) =>
    API.post("/results/upload-excel", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
};

// Contact API
export const contactAPI = {
  submit: (data) => API.post("/contact", data),
  getAll: (params) => API.get("/contact", { params }),
  markAsRead: (id) => API.patch(`/contact/${id}/read`),
  delete: (id) => API.delete(`/contact/${id}`),
};

// Site Settings API
export const settingsAPI = {
  get: () => API.get("/settings"),
  updateLogo: (data) =>
    API.put("/settings/logo", data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  removeLogo: () => API.delete("/settings/logo"),
  updateCampusImage: (data) =>
    API.put("/settings/campus-image", data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  updateHeroMedia: (data) =>
    API.put("/settings/hero-media", data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  removeHeroMedia: () => API.delete("/settings/hero-media"),
  uploadLeadershipPhoto: (data) =>
    API.post("/settings/leadership-photo", data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  updateTestimonials: (data) => API.put("/settings/testimonials", data),
  updateLeadership: (data) => API.put("/settings/leadership", data),
  updateAchievements: (data) => API.put("/settings/achievements", data),
  updateFeeStructure: (data) => API.put("/settings/fee-structure", data),
  updatePrograms: (data) => API.put("/settings/programs", data),
  updateAcademicCalendar: (data) =>
    API.put("/settings/academic-calendar", data),
  updatePopupBanner: (data) =>
    API.put("/settings/popup-banner", data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  removePopupBanner: () => API.delete("/settings/popup-banner"),
};

// Dashboard API - all data comes from single /stats endpoint
export const dashboardAPI = {
  getStats: () => API.get("/dashboard/stats"),
};

export default API;
